import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import type { InboxItem } from "@/hooks/useInbox";
import type { ConceptListItem } from "@/types/concept";
import type { WorkingSet } from "@/hooks/useWorkingSets";

interface Props {
  item: InboxItem;
  concepts: ConceptListItem[];
  workingSets?: Record<string, WorkingSet[]>;
  onAssign: (itemId: string, conceptId: string, workingSetId?: string) => void;
  onDismiss: (itemId: string) => void;
  onDefer: (itemId: string) => void;
}

export function InboxCard({ item, concepts, workingSets, onAssign, onDismiss, onDefer }: Props) {
  const abstractSnippet = item.paper.abstract
    ? item.paper.abstract.split(/\.\s+/).slice(0, 2).join(". ") + "."
    : null;

  return (
    <div className="rounded-lg border p-4 space-y-2">
      <div>
        <h4 className="font-semibold text-sm leading-snug">{item.paper.title}</h4>
        <p className="text-xs text-muted-foreground">
          {item.paper.authors?.slice(0, 3).join(", ")}
          {(item.paper.authors?.length ?? 0) > 3 ? " et al." : ""}
          {item.paper.year ? ` (${item.paper.year})` : ""}
        </p>
      </div>

      {abstractSnippet && (
        <p className="text-xs text-muted-foreground line-clamp-3">{abstractSnippet}</p>
      )}

      {item.source_concept_name && (
        <div className="flex items-center gap-1">
          <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-600">
            Flagged from:{" "}
            <Link
              to="/concepts/$conceptId/curate"
              params={{ conceptId: item.source_concept_id! }}
              className="underline"
            >
              {item.source_concept_name}
            </Link>
          </span>
        </div>
      )}

      {item.user_note && (
        <p className="text-xs italic text-muted-foreground">"{item.user_note}"</p>
      )}

      <InboxCardActions
        item={item}
        concepts={concepts}
        workingSets={workingSets}
        onAssign={onAssign}
        onDismiss={onDismiss}
        onDefer={onDefer}
      />
    </div>
  );
}

function InboxCardActions({
  item,
  concepts,
  workingSets,
  onAssign,
  onDismiss,
  onDefer,
}: {
  item: InboxItem;
  concepts: ConceptListItem[];
  workingSets?: Record<string, WorkingSet[]>;
  onAssign: (itemId: string, conceptId: string, workingSetId?: string) => void;
  onDismiss: (itemId: string) => void;
  onDefer: (itemId: string) => void;
}) {
  const [selectedConceptId, setSelectedConceptId] = useState("");
  const [selectedWsId, setSelectedWsId] = useState("");
  const conceptWsList = selectedConceptId && workingSets ? workingSets[selectedConceptId] ?? [] : [];

  const sortedConcepts = concepts
    .filter((c) => c.id !== item.source_concept_id)
    .sort((a, b) => {
      if (a.id === item.suggested_concept_id) return -1;
      if (b.id === item.suggested_concept_id) return 1;
      return 0;
    });

  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <select
        className="rounded border bg-background px-2 py-1 text-xs"
        value={selectedConceptId}
        onChange={(e) => { setSelectedConceptId(e.target.value); setSelectedWsId(""); }}
      >
        <option value="" disabled>Add to concept...</option>
        {sortedConcepts.map((c) => (
          <option key={c.id} value={c.id}>
            {c.id === item.suggested_concept_id ? `★ ${c.name} (suggested)` : c.name}
          </option>
        ))}
      </select>
      {conceptWsList.length > 0 && (
        <select
          className="rounded border bg-background px-2 py-1 text-xs"
          value={selectedWsId}
          onChange={(e) => setSelectedWsId(e.target.value)}
        >
          <option value="">No working set</option>
          {conceptWsList.map((ws) => (
            <option key={ws.id} value={ws.id}>{ws.name}</option>
          ))}
        </select>
      )}
      {selectedConceptId && (
        <Button
          variant="outline"
          size="xs"
          onClick={() => onAssign(item.id, selectedConceptId, selectedWsId || undefined)}
        >
          Assign
        </Button>
      )}
      <Button variant="ghost" size="xs" onClick={() => onDismiss(item.id)}>
        Dismiss
      </Button>
      <Button variant="ghost" size="xs" onClick={() => onDefer(item.id)}>
        Defer
      </Button>
    </div>
  );
}
